import { type FC, type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { ForgeAutocomplete, ForgeChip, ForgeChipField } from '@tylertech/forge-react';
import uniq from 'lodash/uniq';
import { type IBaseListDropdownOption } from '@tylertech/forge/esm/list-dropdown';
import { type View } from 'common/types/view';
import airbrake from 'common/airbrake';
import Spinner from '../../../Spinner';
import { I18n } from 'common/i18n';
import { type ScheduleSendValues } from '../../types/scheduleSend';
import { isEmpty } from 'lodash';
import { fetchUsers } from '../../services';

const scope = 'shared.components.schedule_send_report.modal';

type Props = {
  view: View;
  scheduledSendModalVisible: boolean;
  recipients: IBaseListDropdownOption[];
  readOnly: boolean;
  updateScheduleSendValue: <K extends keyof ScheduleSendValues>(key: K, value: ScheduleSendValues[K]) => void;
  isRecipientEmptyUponSaving: boolean;
};

export const RecipientsField: FC<Props> = (props) => {
  const {
    view,
    scheduledSendModalVisible,
    recipients,
    updateScheduleSendValue,
    readOnly,
    isRecipientEmptyUponSaving,
  } = props;
  const [fetchedUsers, setFetchedUsers] = useState<IBaseListDropdownOption[]>([]);
  const [isFetchingUsers, setIsFetchingUsers] = useState<boolean>(true);
  const usersInputRef = useRef<HTMLInputElement>() as MutableRefObject<HTMLInputElement>;
  const [isRecipientsInvalid, setIsRecipientsInvalid] = useState(false);

  const onRemoveSelectedRecipient = useCallback(
    (recipientToRemove: IBaseListDropdownOption) => {
      const updatedRecipients = recipients.filter((recipient) => recipient.value !== recipientToRemove.value);
      updateScheduleSendValue('recipients', updatedRecipients);

      if (updatedRecipients.length === 0) {
        setIsRecipientsInvalid(true);
      }
    },
    [recipients, updateScheduleSendValue],
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!scheduledSendModalVisible) {
      return;
    }

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;
    let isMounted = true;

    // Always reset state when modal opens
    setIsFetchingUsers(true);
    setFetchedUsers([]);

    const loadUsers = async () => {
      try {
        const results = await fetchUsers(view, { signal });

        if (!signal.aborted && isMounted) {
          setFetchedUsers(
            results.map((result) => ({
              label: result.email as string,
              value: result.id,
            })),
          );
          setIsFetchingUsers(false);
        }
      } catch (error) {
        if (!signal.aborted && isMounted) {
          airbrake.notify({ error, context: { component: 'ScheduleSendReport/RecipientsField' } });
          setIsFetchingUsers(false);
        }
      }
    };

    loadUsers();

    // Cleanup function
    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [scheduledSendModalVisible, view]);

  const hasRecipientsError = (isEmpty(recipients) && isRecipientEmptyUponSaving) || isRecipientsInvalid;

  return (
    <div data-testid="recipients-field">
      {isFetchingUsers && !readOnly ? (
        <Spinner />
      ) : (
        <ForgeAutocomplete
          mode="stateless"
          data-testid="recipients-field"
          filterOnFocus={false}
          debounce={500}
          filter={(search: string) => {
            if (search === '') {
              return [];
            }
            return fetchedUsers.filter((fetchedUser) => {
              const isAlreadySelected = recipients.some((recipient) => recipient.value === fetchedUser.value);
              const matchesSearch = fetchedUser.label.toLowerCase().includes(search.toLocaleLowerCase());

              return matchesSearch && !isAlreadySelected;
            });
          }}
          value={fetchedUsers}
          on-forge-autocomplete-select={(event: CustomEvent) => {
            setIsRecipientsInvalid(false);
            const matchedUser = fetchedUsers.find((fetchedUser) => fetchedUser.value === event.detail.value);
            if (!matchedUser) {
              return;
            }
            updateScheduleSendValue('recipients', uniq([...recipients, matchedUser]));
            usersInputRef.current.value = '';
          }}
          on-forge-chip-field-member-added={(event: CustomEvent) => {
            const matchedUser = fetchedUsers.find((fetchedUser) => fetchedUser.label === event.detail);
            if (matchedUser) {
              setIsRecipientsInvalid(false);
              updateScheduleSendValue('recipients', uniq([...recipients, matchedUser]));
            }
          }}
        >
          <ForgeChipField
            data-testid="recipients-chip-field"
            labelPosition="block-start"
            required={!readOnly}
            disabled={readOnly}
            invalid={hasRecipientsError}
          >
            <label slot="label" htmlFor="recipients-field">
              {I18n.t('recipients_label', { scope })}
            </label>
            <input
              id="recipients-field"
              data-testid="recipients-field-input"
              type="text"
              ref={usersInputRef}
              autoComplete="off"
              readOnly={readOnly}
              onBlur={() => {
                if (usersInputRef.current) {
                  usersInputRef.current.value = '';
                }
              }}
            />
            {hasRecipientsError && <span slot="support-text">{I18n.t('required_field', { scope })}</span>}
            {recipients.map((recipient) => (
              <ForgeChip
                key={recipient.value}
                slot="member"
                type="field"
                dense
                on-forge-chip-delete={() => {
                  if (!readOnly) {
                    onRemoveSelectedRecipient(recipient);
                  }
                }}
                value={recipient.label}
                data-testid={`chip-${recipient.value}`}
                disabled={readOnly}
              >
                {recipient.label}
              </ForgeChip>
            ))}
          </ForgeChipField>
        </ForgeAutocomplete>
      )}
    </div>
  );
};
